// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./EditRoleDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// API IMPORTS
import { postAPICall, deleteAPICall } from "../../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

// REACT COMPONENT
const EditRoleDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	// Record states
	const [id, setId] = useState(0);

	const [selectedTools, setSelectedTools] = useState([]);
	const [selectedAdminTools, setSelectedAdminTools] = useState([]);

	// Feedback states
	const [isLoading, setIsLoading] = useState(false);

	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		if (props.open) {
			setId(props.recordToEdit.id);
			setSelectedTools(props.selectedTools);
			setSelectedAdminTools(props.selectedAdminTools);
		}
	}, [props.open, props.recordToEdit]);

	// INPUT HANDLERS
	// Form input handlers
	const toolSelected = (event, newValue) => {
		setSelectedTools([...newValue]);
	};

	const adminToolSelected = (event, newValue) => {
		setSelectedAdminTools([...newValue]);
	};

	// Feedback handlers
	const openAlert = (message) => {
		setAlertOpen(true);
		setAlertMessage(message);
	};

	const closeAlert = () => {
		setAlertOpen(false);
		setAlertMessage("");
	};

	// Submit button handler
	const submitButtonClicked = async () => {
		if (!validateInputFields()) return;

		setIsLoading(true);

		try {
			await removeTools();
			await addTools();
		} catch (err) {
			openAlert(err);
		}

		props.fetchRoles();
		formClosed();

		setIsLoading(false);
	};

	// API FUNCTIONS
	const removeTools = async () => {
		const res = await deleteAPICall(
			instance,
			accounts[0],
			"/api/roles/tools/deleteByRole",
			{
				roleID: id,
			}
		);

		return res;
	};

	const addTools = async () => {
		for (let i = 0; i < selectedTools.length; i++) {
			await postAPICall(instance, accounts[0], "/api/roles/tools/add", {
				roleID: id,
				toolID: selectedTools[i].id,
				modifiedBy: employeeID,
			});
		}

		for (let i = 0; i < selectedAdminTools.length; i++) {
			await postAPICall(
				instance,
				accounts[0],
				"/api/roles/tools/admin/add",
				{
					roleID: id,
					toolID: selectedAdminTools[i].id,
					modifiedBy: employeeID,
				}
			);
		}
	};

	// HELPER FUNCTIONS
	const resetForm = () => {
		closeAlert();

		setId(0);
	};

	const validateInputFields = () => {
		if (selectedTools.length === 0 && selectedAdminTools.length === 0) {
			openAlert("Please select at least one tool.");
			return false;
		}

		if (
			hasCommonElements(
				selectedTools.map((t) => t.id),
				selectedAdminTools.map((t) => t.id)
			)
		) {
			openAlert(
				"Tools and Tools w/ Admin Access cannot have common elements."
			);
			return false;
		}

		return true;
	};

	const formClosed = () => {
		resetForm();
		props.editRoleDialogClosed();
	};

	const hasCommonElements = (arr1, arr2) => {
		return arr1.some((element) => arr2.includes(element));
	};

	// RENDER
	return (
		<Dialog
			id="editRoleDialog"
			open={props.open}
			fullWidth
			onClose={formClosed}
		>
			<DialogTitle id="editRoleDialogTitle">
				{props.recordToEdit.role_name}
			</DialogTitle>
			<DialogContent id="editRoleDialogContent">
				{isLoading && (
					<div className="loadingSpinnerContainer">
						<CircularProgress color="primary" />
					</div>
				)}
				{!isLoading && (
					<>
						<div className="editRoleDialogRow">
							<Collapse
								sx={{ flex: 1 }}
								id="editRoleDialogCollapse"
								in={alertOpen}
							>
								<Alert
									id="editRoleDialogAlert"
									severity="error"
									action={
										<IconButton
											id="editRoleDialogCloseIconButton"
											aria-label="close"
											color="inherit"
											size="small"
											onClick={closeAlert}
										>
											<CloseIcon
												id="editRoleDialogCloseIcon"
												fontSize="inherit"
											/>
										</IconButton>
									}
								>
									{alertMessage}
								</Alert>
							</Collapse>
						</div>
						<div className="editRoleDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								limitTags={2}
								onChange={toolSelected}
								multiple
								value={selectedTools}
								id="roleSelect"
								options={props.tools.filter(
									(tool) =>
										!selectedTools
											.map((t) => t.id)
											.includes(tool.id)
								)}
								getOptionLabel={(option) => option.name}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField
										{...params}
										label="Tools"
										placeholder="Tools"
									/>
								)}
							/>
						</div>
						<div className="editRoleDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								limitTags={2}
								onChange={adminToolSelected}
								multiple
								value={selectedAdminTools}
								id="adminRoleSelect"
								options={props.tools.filter(
									(tool) =>
										!selectedAdminTools
											.map((t) => t.id)
											.includes(tool.id)
								)}
								getOptionLabel={(option) => option.name}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField
										{...params}
										label="Tools w/ Admin Access"
										placeholder="Tools"
									/>
								)}
							/>
						</div>
						<div className="dialogButtons">
							<Button
								onClick={formClosed}
								variant="outlined"
								color="error"
							>
								Cancel
							</Button>
							<Button
								onClick={submitButtonClicked}
								variant="contained"
							>
								Submit
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default EditRoleDialog;
